import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  Badge,
  Tooltip
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today,
  Umbrella,
  BeachAccess,
  Cake,
  Celebration
} from '@mui/icons-material';

interface CalendarEvent {
  date: string; // YYYY-MM-DD format
  type: 'holiday' | 'leave' | 'meeting' | 'birthday' | 'anniversary';
  title: string;
}

interface MiniCalendarProps {
  events?: CalendarEvent[];
  onDateClick?: (date: Date) => void;
  highlightedDates?: string[];
}

const MiniCalendar: React.FC<MiniCalendarProps> = ({
  events = [],
  onDateClick,
  highlightedDates = []
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [currentDate, setCurrentDate] = useState(new Date());

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDateKey = (date: Date) => {
    // Use local date components to avoid timezone issues with toISOString()
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const eventsByDate = useMemo(() => {
    const map: { [key: string]: CalendarEvent[] } = {};
    events.forEach(event => {
      if (!map[event.date]) {
        map[event.date] = [];
      }
      map[event.date].push(event);
    });
    return map;
  }, [events]);

  const calendarDays = useMemo(() => {
    const days: (number | null)[] = [];
    const firstDay = getFirstDayOfMonth(currentDate);
    const daysInMonth = getDaysInMonth(currentDate);

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  }, [currentDate]);

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = (day: number) => {
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const getEventsForDay = (day: number) => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const dateKey = formatDateKey(date);
    return eventsByDate[dateKey] || [];
  };

  const isHighlighted = (day: number) => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const dateKey = formatDateKey(date);
    return highlightedDates.includes(dateKey);
  };

  const handleDayClick = (day: number) => {
    if (onDateClick) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      onDateClick(date);
    }
  };

  const getEventColor = (type: CalendarEvent['type']) => {
    switch (type) {
      case 'holiday':
        return '#F44336';
      case 'leave':
        return '#FF9800';
      case 'meeting':
        return '#2196F3';
      case 'birthday':
        return '#E91E63';
      case 'anniversary':
        return '#9C27B0';
      default:
        return '#4CAF50';
    }
  };

  const getEventIcon = (type: CalendarEvent['type'], color: string) => {
    const iconStyle = { fontSize: 10, color };
    switch (type) {
      case 'holiday':
        return <Umbrella sx={iconStyle} />;
      case 'leave':
        return <BeachAccess sx={iconStyle} />;
      case 'birthday':
        return <Cake sx={iconStyle} />;
      case 'anniversary':
        return <Celebration sx={iconStyle} />;
      default:
        return null;
    }
  };

  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <IconButton size="small" onClick={goToPreviousMonth}>
          <ChevronLeft />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </Typography>
          <Tooltip title="Go to today">
            <IconButton size="small" onClick={goToToday}>
              <Today fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <IconButton size="small" onClick={goToNextMonth}>
          <ChevronRight />
        </IconButton>
      </Box>

      {/* Week days header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 0.5,
          mb: 1
        }}
      >
        {weekDays.map(day => (
          <Typography
            key={day}
            variant="caption"
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              color: theme.palette.text.secondary,
              py: 0.5
            }}
          >
            {day}
          </Typography>
        ))}
      </Box>

      {/* Calendar days */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 0.5
        }}
      >
        {calendarDays.map((day, index) => {
          if (day === null) {
            return <Box key={`empty-${index}`} sx={{ aspectRatio: '1', p: 0.5 }} />;
          }

          const dayEvents = getEventsForDay(day);
          const highlighted = isHighlighted(day);
          const isTodayDate = isToday(day);

          return (
            <Tooltip
              key={day}
              title={
                dayEvents.length > 0
                  ? dayEvents.map(e => e.title).join(', ')
                  : ''
              }
              arrow
              disableHoverListener={dayEvents.length === 0}
            >
              <Box
                onClick={() => handleDayClick(day)}
                sx={{
                  aspectRatio: '1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  cursor: onDateClick ? 'pointer' : 'default',
                  position: 'relative',
                  backgroundColor: isTodayDate
                    ? theme.palette.primary.main
                    : highlighted
                      ? theme.palette.primary.light + '30'
                      : 'transparent',
                  color: isTodayDate
                    ? '#fff'
                    : theme.palette.text.primary,
                  fontWeight: isTodayDate ? 700 : 400,
                  transition: 'all 0.2s',
                  '&:hover': onDateClick ? {
                    backgroundColor: isTodayDate
                      ? theme.palette.primary.dark
                      : theme.palette.action.hover
                  } : {}
                }}
              >
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  {day}
                </Typography>

                {/* Event indicators with icons */}
                {dayEvents.length > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 1,
                      display: 'flex',
                      gap: '1px',
                      justifyContent: 'center'
                    }}
                  >
                    {dayEvents.slice(0, 3).map((event, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center' }}>
                        {getEventIcon(event.type, getEventColor(event.type))}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* Legend with icons */}
      {events.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {['holiday', 'leave', 'birthday', 'anniversary'].map(type => (
            <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {getEventIcon(type as CalendarEvent['type'], getEventColor(type as CalendarEvent['type']))}
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'capitalize' }}>
                {type}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default MiniCalendar;
